<template>
  <div class="cyber-button-container" 
       ref="container"
       :class="{ 'is-dragging': isDragging }"
       :style="{ right: position.x + 'px', bottom: position.y + 'px' }"
       @touchstart="handleTouchStart"
       @touchmove="handleTouchMove"
       @touchend="handleTouchEnd"
       @mousedown="handleMouseDown">
    <!-- 收起状态：只显示按钮图片 -->
    <div v-if="!isExpanded" class="collapsed-button" @click="expand">
      <img src="@/assets/images/cyber-button/03_button.png" alt="赛博按钮" class="button-image-small" draggable="false" />
    </div>
    
    <!-- 展开状态：显示完整功能 -->
    <div v-else class="expanded-panel">
      <!-- 控制按钮区域 -->
      <div class="control-buttons">
        <el-tooltip placement="top" effect="dark">
          <div slot="content" class="stats-tooltip">
            <div class="tooltip-title">当前数值：{{ formatNumber(totalStats) }}</div>
            <div class="stats-grid">
              <div class="stats-column">
                <div v-for="item in leftColumnStats" :key="item.key" class="stat-item">
                  <span class="stat-label">{{ item.key }}:</span>
                  <span class="stat-value">{{ item.key === maxStatKey ? '👑 ' : '' }}{{ formatNumber(item.value) }}</span>
                </div>
              </div>
              <div class="stats-column">
                <div v-for="item in rightColumnStats" :key="item.key" class="stat-item">
                  <span class="stat-label">{{ item.key }}:</span>
                  <span class="stat-value">{{ item.key === maxStatKey ? '👑 ' : '' }}{{ formatNumber(item.value) }}</span>
                </div>
              </div>
            </div>
          </div>
          <el-button size="mini" type="primary" icon="el-icon-data-analysis" circle></el-button>
        </el-tooltip>
        <el-tooltip content="收起" placement="top">
          <el-button size="mini" type="info" icon="el-icon-arrow-down" circle @click="collapse"></el-button>
        </el-tooltip>
      </div>
      
      <!-- 按钮图片区域 -->
      <div class="button-wrapper" 
           @click="handleClick">
        <img src="@/assets/images/cyber-button/03_button.png" alt="赛博按钮" class="button-image" draggable="false" />
      </div>
      
      <!-- 飘字效果 - 支持多个同时显示 -->
      <transition-group name="float-up" tag="div" class="float-text-container">
        <div 
          v-for="item in floatTexts" 
          :key="item.id" 
          :class="['float-text', item.effect === 'super_double' ? 'super-double-text' : '']"
          :style="getFloatTextStyle(item)">
          {{ item.text }}
        </div>
      </transition-group>
    </div>
  </div>
</template>

<script>
import request from '@/utils/request'
import { isMobile } from '@/utils/device'

export default {
  name: 'CyberButton',
  data() {
    return {
      isExpanded: true, // 默认展开（PC端），移动端在mounted中设置为false
      isOnCooldown: false, // 是否在冷却中
      stats: {}, // 统计数据
      floatTexts: [], // 飘字队列
      floatTextIdCounter: 0, // 飘字ID计数器
      // 拖拽相关
      position: { x: 20, y: 20 }, // 位置
      isDragging: false, // 是否正在拖拽
      dragStart: null, // 拖拽起始位置（null表示未开始拖拽）
      dragOffset: { x: 0, y: 0 } // 拖拽偏移量
    }
  },
  computed: {
    // 显示的统计数据（中文标签）
    displayStats() {
      return {
        'RP': this.stats.rp || 0,
        '心情': this.stats.mood || 0,
        '心态': this.stats.mentality || 0,
        '决心': this.stats.determination || 0,
        '信心': this.stats.confidence || 0,
        '体力': this.stats.stamina || 0,
        '码力': this.stats.coding_power || 0,
        '数据结构': this.stats.data_structure || 0,
        '基础算法': this.stats.basic_algorithm || 0,
        '动态规划': this.stats.dynamic_programming || 0,
        '组合计数': this.stats.combinatorics || 0,
        '字符串': this.stats.string_algorithm || 0,
        '图论': this.stats.graph_theory || 0,
        '文化课': this.stats.liberal_arts || 0
      }
    },
    // 按固定顺序转换为数组
    displayStatsArray() {
      return [
        { key: 'RP', value: this.stats.rp || 0 },
        { key: '心情', value: this.stats.mood || 0 },
        { key: '心态', value: this.stats.mentality || 0 },
        { key: '决心', value: this.stats.determination || 0 },
        { key: '信心', value: this.stats.confidence || 0 },
        { key: '体力', value: this.stats.stamina || 0 },
        { key: '码力', value: this.stats.coding_power || 0 },
        { key: '数据结构', value: this.stats.data_structure || 0 },
        { key: '基础算法', value: this.stats.basic_algorithm || 0 },
        { key: '动态规划', value: this.stats.dynamic_programming || 0 },
        { key: '组合计数', value: this.stats.combinatorics || 0 },
        { key: '字符串', value: this.stats.string_algorithm || 0 },
        { key: '图论', value: this.stats.graph_theory || 0 },
        { key: '文化课', value: this.stats.liberal_arts || 0 }
      ]
    },
    // 左列数据（前一半）
    leftColumnStats() {
      const array = this.displayStatsArray
      const mid = Math.ceil(array.length / 2)
      return array.slice(0, mid)
    },
    // 右列数据（后一半）
    rightColumnStats() {
      const array = this.displayStatsArray
      const mid = Math.ceil(array.length / 2)
      return array.slice(mid)
    },
    // 计算所有数值之和
    totalStats() {
      return Object.values(this.displayStats).reduce((sum, val) => sum + val, 0)
    },
    // 数值最大的那一项的 key（用于显示 👑）
    maxStatKey() {
      const arr = this.displayStatsArray
      if (!arr.length) return null
      let maxItem = arr[0]
      for (let i = 1; i < arr.length; i++) {
        if (arr[i].value > maxItem.value) maxItem = arr[i]
      }
      return maxItem.key
    }
  },
  mounted() {
    // 移动端默认收起
    if (isMobile()) {
      this.isExpanded = false
    } else {
      // PC端默认位置向左移动100px，避免文字被浏览器边缘遮挡
      this.position.x = 120
    }
    this.loadStats()
  },
  beforeDestroy() {
    // 清理全局事件监听器
    document.removeEventListener('mousemove', this.handleMouseMoveDocument)
    document.removeEventListener('mouseup', this.handleMouseUpDocument)
  },
  methods: {
    // 数字千位分隔格式化
    formatNumber(value) {
      if (value === null || value === undefined) return 0
      const num = Number(value)
      if (Number.isNaN(num)) return value
      return num.toLocaleString('zh-CN')
    },
    
    // 获取飘字样式（根据增加值/特效设置颜色）
    getFloatTextStyle(item) {
      let color = '#FFD700' // 默认金色
      let shadowColor = 'rgba(255, 215, 0, 0.8)'
      
      // 特殊效果颜色设置
      if (item.effect === 'rp_plus') {
        // RP++: 金色
        color = '#FFD700'
        shadowColor = 'rgba(255, 215, 0, 0.8)'
      } else if (item.effect === 'super_double') {
        // 超级加倍: 七彩色（使用渐变）- 鲜艳显眼
        // 使用渐变作为文字颜色，但保持相同的阴影效果
        return {
          background: 'linear-gradient(120deg, #ff00ff, #ff0080, #ff4000, #ffff00, #00ff00, #00ffff, #0080ff, #8000ff, #ff00ff)',
          backgroundSize: '200% 200%',
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          color: 'transparent',
          textShadow: `
            0 0 20px rgba(255, 0, 255, 1),
            0 0 30px rgba(255, 0, 128, 1),
            0 0 40px rgba(255, 64, 0, 1),
            0 0 50px rgba(255, 255, 0, 1),
            2px 2px 4px rgba(0, 0, 0, 0.9)
          `
        }
      } else {
        // 普通效果：根据增加值设置不同颜色
        if (item.increment >= 1 && item.increment <= 20) {
          // 1-20: 绿色
          color = '#67C23A'
          shadowColor = 'rgba(103, 194, 58, 0.8)'
        } else if (item.increment >= 21 && item.increment <= 50) {
          // 21-50: 蓝色
          color = '#409EFF'
          shadowColor = 'rgba(64, 158, 255, 0.8)'
        } else if (item.increment >= 51 && item.increment <= 80) {
          // 51-80: 紫色
          color = '#C084FC'
          shadowColor = 'rgba(192, 132, 252, 0.8)'
        } else {
          // 81-99: 金色
          color = '#FFD700'
          shadowColor = 'rgba(255, 215, 0, 0.8)'
        }
      }
      
      return {
        color: color,
        textShadow: `
          0 0 10px ${shadowColor},
          0 0 20px ${shadowColor},
          0 0 30px ${shadowColor},
          2px 2px 4px rgba(0, 0, 0, 0.8)
        `
      }
    },
    
    // 加载统计数据
    async loadStats() {
      try {
        const response = await request({
          url: '/api/cyber-button/stats',
          method: 'get'
        })
        if (response.code === 200) {
          this.stats = response.data
        }
      } catch (error) {
        console.error('加载赛博按钮数据失败:', error)
      }
    },
    
    // 展开
    expand() {
      // 如果正在拖拽，不触发展开
      if (this.isDragging) {
        return
      }
      this.isExpanded = true
    },
    
    // 收起
    collapse() {
      this.isExpanded = false
    },
    
    // 触摸开始（拖拽）
    handleTouchStart(e) {
      this.isDragging = false
      this.dragStart = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY
      }
      this.dragOffset = {
        x: this.position.x,
        y: this.position.y
      }
    },
    
    // 触摸移动（拖拽）
    handleTouchMove(e) {
      const moveX = Math.abs(e.touches[0].clientX - this.dragStart.x)
      const moveY = Math.abs(e.touches[0].clientY - this.dragStart.y)
      
      // 移动距离超过 10px 才认为是拖拽
      if (moveX > 10 || moveY > 10) {
        this.isDragging = true
        e.preventDefault()
        
        const deltaX = this.dragStart.x - e.touches[0].clientX
        const deltaY = this.dragStart.y - e.touches[0].clientY  // 反转 Y 轴（因为使用 bottom 定位）
        
        // 计算新位置（限制在视口内）
        const newX = Math.max(0, Math.min(window.innerWidth - 100, this.dragOffset.x + deltaX))
        const newY = Math.max(0, Math.min(window.innerHeight - 100, this.dragOffset.y + deltaY))
        
        this.position.x = newX
        this.position.y = newY
      }
    },
    
    // 触摸结束（拖拽）
    handleTouchEnd(e) {
      // 如果是拖拽，不触发点击
      if (this.isDragging) {
        this.isDragging = false
        return
      }
    },
    
    // 鼠标按下（PC端拖拽开始）
    handleMouseDown(e) {
      // 只处理鼠标左键
      if (e.button !== 0) return
      
      // 如果是移动端，不处理（移动端使用触摸事件）
      if (isMobile()) {
        return
      }
      
      this.isDragging = false
      this.dragStart = {
        x: e.clientX,
        y: e.clientY
      }
      this.dragOffset = {
        x: this.position.x,
        y: this.position.y
      }
      
      // 防止文本选择
      e.preventDefault()
      
      // 添加全局事件监听
      document.addEventListener('mousemove', this.handleMouseMoveDocument)
      document.addEventListener('mouseup', this.handleMouseUpDocument)
    },
    
    // 鼠标移动（全局，用于拖拽）
    handleMouseMoveDocument(e) {
      if (!this.dragStart) {
        return
      }
      
      const moveX = Math.abs(e.clientX - this.dragStart.x)
      const moveY = Math.abs(e.clientY - this.dragStart.y)
      
      // 移动距离超过 5px 才认为是拖拽
      if (moveX > 5 || moveY > 5) {
        this.isDragging = true
        
        const deltaX = this.dragStart.x - e.clientX
        const deltaY = this.dragStart.y - e.clientY  // 反转 Y 轴（因为使用 bottom 定位）
        
        // 计算新位置（限制在视口内）
        const containerWidth = this.$refs.container?.offsetWidth || 150
        const containerHeight = this.$refs.container?.offsetHeight || 200
        const newX = Math.max(0, Math.min(window.innerWidth - containerWidth, this.dragOffset.x + deltaX))
        const newY = Math.max(0, Math.min(window.innerHeight - containerHeight, this.dragOffset.y + deltaY))
        
        this.position.x = newX
        this.position.y = newY
      }
    },
    
    // 鼠标释放（全局，结束拖拽）
    handleMouseUpDocument(e) {
      const wasDragging = this.isDragging
      
      // 移除全局事件监听
      document.removeEventListener('mousemove', this.handleMouseMoveDocument)
      document.removeEventListener('mouseup', this.handleMouseUpDocument)
      
      // 延迟重置拖拽状态，确保点击事件能正确判断
      if (wasDragging) {
        setTimeout(() => {
          this.isDragging = false
        }, 50)
      } else {
        this.isDragging = false
      }
      
      this.dragStart = null
      this.dragOffset = { x: 0, y: 0 }
    },
    
    // 点击按钮
    async handleClick() {
      if (this.isOnCooldown || this.isDragging) {
        return
      }
      
      this.isOnCooldown = true
      
      try {
        const response = await request({
          url: '/api/cyber-button/click',
          method: 'post'
        })
        
        if (response.code === 200) {
          const { attribute, increment, stats, effect_type, effect_label } = response.data
          
          // 更新统计数据
          this.stats = stats
          
          // 添加新的飘字到队列
          const floatTextId = ++this.floatTextIdCounter
          const isSpecialEffect = !!effect_type
          const displayText = isSpecialEffect 
            ? `${effect_label} · 全属性 +${increment}`
            : `${attribute} +${increment}`
          const floatTextItem = {
            id: floatTextId,
            text: displayText,
            increment: increment,
            effect: effect_type || null
          }
          this.floatTexts.push(floatTextItem)
          
          // 3秒后从队列中移除这个飘字（与动画时长一致）
          setTimeout(() => {
            const index = this.floatTexts.findIndex(item => item.id === floatTextId)
            if (index > -1) {
              this.floatTexts.splice(index, 1)
            }
          }, 3000)
        }
      } catch (error) {
        console.error('点击赛博按钮失败:', error)
        this.$message.error('点击失败，请稍后重试')
      } finally {
        // 50ms 冷却时间
        setTimeout(() => {
          this.isOnCooldown = false
        }, 50)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.cyber-button-container {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 9999;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  touch-action: none;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  
  // PC端可拖拽
  @media (min-width: 769px) {
    cursor: move;
    
    // 按钮区域保持pointer
    .button-wrapper {
      cursor: pointer !important;
    }
    
    // 控制按钮区域保持默认
    .control-buttons {
      cursor: default;
    }
    
    // 拖拽时显示grabbing cursor
    &.is-dragging {
      cursor: grabbing;
      
      .button-wrapper {
        cursor: grabbing !important;
      }
    }
  }
  
  // 收起状态
  .collapsed-button {
    cursor: pointer;
    transition: all 0.3s ease;
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    -webkit-tap-highlight-color: transparent;
    -webkit-touch-callout: none;
    
    &:hover {
      transform: scale(1.05);
    }
    
    .button-image-small {
      width: 50px;
      height: 50px;
      display: block;
      user-select: none;
      -webkit-user-drag: none;
      pointer-events: none;
      -webkit-tap-highlight-color: transparent;
    }
  }
  
  // 展开状态
  .expanded-panel {
    position: relative;
    
    // 控制按钮
    .control-buttons {
      display: flex;
      gap: 8px;
      justify-content: flex-end;
      margin-bottom: 8px;
    }
    
    // 按钮图片
    .button-wrapper {
      position: relative;
      cursor: pointer;
      transition: transform 0.2s ease;
      user-select: none;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      -webkit-tap-highlight-color: transparent;
      -webkit-touch-callout: none;
      
      &:hover {
        transform: scale(1.05);
      }
      
      &:active {
        transform: scale(0.95);
      }
      
      .button-image {
        width: 150px;
        height: 150px;
        display: block;
        user-select: none;
        -webkit-user-drag: none;
        pointer-events: none;
        -webkit-tap-highlight-color: transparent;
      }
    }
    
    // 飘字容器
    .float-text-container {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 10000;
    }
    
    // 飘字效果
    .float-text {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 32px;
      font-weight: bold;
      pointer-events: none;
      white-space: nowrap;
      z-index: 10000;
      text-transform: none;
    }
  }
}

// 飘字动画 - 确保完整执行不被中断
.float-up-enter-active {
  animation: floatUp 3s ease-out forwards;
  
  // 超级加倍文本：同时应用飘动和彩虹动画
  &.super-double-text {
    animation: floatUp 3s ease-out forwards, rainbow-shift 3s linear infinite;
  }
}

.float-up-leave-active {
  opacity: 0;
  transition: none;
}

@keyframes floatUp {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) translateY(0);
  }
  10% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -50%) translateY(-450px);
  }
}

// 彩虹渐变动画
@keyframes rainbow-shift {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

// 移动端样式
@media (max-width: 768px) {
  .cyber-button-container {
    // 收起状态
    .collapsed-button {
      .button-image-small {
        width: 50px;
        height: 50px;
      }
    }
    
    // 展开状态
    .expanded-panel {
      .button-wrapper {
        .button-image {
          width: 150px;
          height: 150px;
        }
      }
      
      // 飘字字体稍小一些
      .float-text {
        font-size: 24px;
      }
    }
  }
  
  // 移动端飘字高度稍低
  @keyframes floatUp {
    0% {
      opacity: 0;
      transform: translate(-50%, -50%) translateY(0);
    }
    10% {
      opacity: 1;
    }
    100% {
      opacity: 0;
      transform: translate(-50%, -50%) translateY(-300px);
    }
  }
}
</style>

<style lang="scss">
// 全局样式，用于 Tooltip
.stats-tooltip {
  min-width: 280px;
  max-height: 400px;
  overflow-y: auto;
  
  .tooltip-title {
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 12px;
    text-align: center;
    color: #FFD700;
    white-space: nowrap;
  }
  
  .stats-grid {
    display: flex;
    gap: 8px;
    
    .stats-column {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    
    .stat-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 4px 8px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 4px;
      min-width: 130px;
      gap: 8px;
      
      .stat-label {
        color: #bfcbd9;
        font-size: 14px;
        white-space: nowrap;
        flex-shrink: 0;
      }
      
      .stat-value {
        color: #FFD700;
        font-weight: bold;
        font-size: 14px;
        text-align: right;
        min-width: 40px;
      }
    }
  }
}
</style>
